import { useTranslation } from 'react-i18next'

import { OkvirTona, razredPolja, type Ton } from './ton'

/**
 * POLJE SATA — 24 sata, bez pregovora (D15-A §4).
 *
 * Nativni `<input type="time">` sat crta po jeziku PREGLEDNIKA, pa je engleskom
 * sučelju nudio "06:30 PM" i onda kad je uređaj hrvatski; `lang="hr"` je pomagao
 * u Chromiumu, a u Firefoxu i na iOS-u ne. Ovdje 12-satnog oblika NEMA jer ga
 * nema odakle biti: sati su nabrojani od `00` do `23`, i to je cijeli izbor.
 *
 * **Dvije kontrole, ne jedna.** Jedan popis sa svih 1440 minuta u danu nije
 * izbor nego traženje; dva popisa (sat, minuta) daju bilo koju minutu u dva
 * dodira. Vrijednost prema van ostaje `HH:mm`, ista koju je nosilo nativno polje.
 *
 * Natpis polja nosi popis SATI — on je prva odluka i prvi na tabu; minuta uz sebe
 * ima vlastito ime za čitač ekrana.
 */

const SATI = Array.from({ length: 24 }, (_, sat) => String(sat).padStart(2, '0'))
const MINUTE = Array.from({ length: 60 }, (_, minuta) => String(minuta).padStart(2, '0'))

/** `HH:mm` → dva dijela; sve što nije taj oblik je prazno, ne pola vrijednosti. */
function razlozi(vrijednost: string): { sat: string; minuta: string } {
  const poklapanje = /^(\d{2}):(\d{2})/.exec(vrijednost)

  return poklapanje ? { sat: poklapanje[1]!, minuta: poklapanje[2]! } : { sat: '', minuta: '' }
}

export function PoljeVremena({
  ton = 'konzola',
  label,
  hint,
  greska,
  obavezno,
  krupno = false,
  natpisSkriven = false,
  value,
  onValue,
  name,
  ...ostalo
}: {
  ton?: Ton
  label: string
  natpisSkriven?: boolean
  hint?: string
  greska?: string
  obavezno?: boolean
  krupno?: boolean
  /** `HH:mm`; prazno = nije izabrano. */
  value: string
  onValue: (vrijednost: string) => void
  name?: string
  [ostalo: string]: unknown
}) {
  const { t } = useTranslation()

  const { sat, minuta } = razlozi(value)

  const razred = razredPolja(ton, krupno, greska)

  /*
    Pola vremena nije vrijeme. Izbor sata nad praznim poljem zato podrazumijeva
    punu minutu, a izbor minute nad praznim satom ponoć — inače bi prvi dodir
    izgledao kao da se ništa nije dogodilo, a drugi bi poslao `:30` bez sata.
  */
  function javi(noviSat: string, novaMinuta: string): void {
    if (noviSat === '' && novaMinuta === '') {
      onValue('')

      return
    }

    onValue(`${noviSat === '' ? '00' : noviSat}:${novaMinuta === '' ? '00' : novaMinuta}`)
  }

  return (
    <OkvirTona
      ton={ton}
      label={label}
      hint={hint}
      greska={greska}
      obavezno={obavezno}
      krupno={krupno}
      natpisSkriven={natpisSkriven}
    >
      {(id, opisano) => (
        <div className="flex items-center gap-2">
          <select
            id={id}
            aria-describedby={opisano}
            aria-invalid={greska ? true : undefined}
            data-picker="sat"
            className={razred}
            required={obavezno}
            name={name}
            value={sat}
            onChange={(dogadaj) => javi(dogadaj.target.value, minuta)}
            {...ostalo}
          >
            <option value="">{t('polje.vrijeme.prazno')}</option>
            {SATI.map((oznaka) => (
              <option key={oznaka} value={oznaka}>
                {oznaka}
              </option>
            ))}
          </select>

          <span aria-hidden="true">:</span>

          <select
            aria-label={t('polje.vrijeme.minuta')}
            data-picker="minuta"
            className={razred}
            value={minuta}
            onChange={(dogadaj) => javi(sat, dogadaj.target.value)}
          >
            <option value="">{t('polje.vrijeme.prazno')}</option>
            {MINUTE.map((oznaka) => (
              <option key={oznaka} value={oznaka}>
                {oznaka}
              </option>
            ))}
          </select>
        </div>
      )}
    </OkvirTona>
  )
}
